import { OrderFooter } from 'order/components/OrderFooter/OrderFooter';
import { Container } from 'shared/components/Container';

import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { FC, useState } from 'react';
import { useAppDispatch } from 'shared/hooks/useAppDispatch';
import { useHistory, useParams } from 'react-router';
import { OrderPageParams } from 'order/interfaces/OrderPageParams';
import { Order } from 'order/interfaces/Order';

import { useHasPermissions } from 'shared/hooks/useHasPermissions';
import { userPermissionsValues } from 'shared/enum/userPermissionsEnum';
import { toast } from 'react-toastify';
import { Spacer } from 'shared/components/Layout';
import { ConfirmationModal } from 'shared/components/ConfirmationModal';
import { ButtonPrimary } from 'shared/components/Button';
import { Wrapper } from 'shared/components/Wrapper';
import { anzac, lynch } from 'shared/config/Colors';

import UtilService from 'shared/services/util.service';
import { CurtainTabsEnum } from 'curtain/enums/CurtainTabsEnum';
import { clearCurtain, setCurtainTab } from 'curtain/store/curtainActions';
import { setOrderAckStatus } from 'order/store/orderActions';
import { SpecialOrderApprovalStatusEnum } from './enums/SpecialOrderApprovalStatusEnum';
import { ACKStatusEnum } from './enums/ACKStatusEnum';
import { changeRequest, changesRequestFinsih } from './store/ACKActions';

export interface AckConsentForm {
  consentGiven: boolean;
  specialOrderApprovalConsents: Record<string, boolean>;
}

export const AcknowledgmentFooter: FC = ({ children }) => {
  const dispatch = useAppDispatch();
  const history = useHistory();

  const { orderId } = useParams<OrderPageParams>();

  const [openModalReqFinish, setOpenModalReqFinish] = useState(false);

  const specialOrderApprovals = useSelector(
    (state: RootState) => state.orderReducer.order?.specialOrderApprovals ?? []
  );

  const hasPendingSOA = specialOrderApprovals.some(
    (soa) => soa.status === SpecialOrderApprovalStatusEnum.PENDING
  );

  const pendingApprovalIds = specialOrderApprovals
    .filter((soa) => soa.status === SpecialOrderApprovalStatusEnum.PENDING)
    .map((soa) => soa.id);

  const latestACK = useSelector(
    (state: RootState) => state.ACKReducer.latestACK
  );

  const isUserCSR = useHasPermissions(
    userPermissionsValues.CSR_VIEW_ORDERBOARD
  );

  const canSendACK = useHasPermissions(
    userPermissionsValues.ACKNOWLEDGEMENT_SEND
  );

  const isCurrentUserViewer = useSelector(
    (state: RootState) => state.orderReducer.isCurrentUserViewer
  );

  const isUserDealerOrSalesRep = useHasPermissions([
    userPermissionsValues.DEALER_VIEW_ORDERBOARD,
    userPermissionsValues.SALES_REPRESENTATIVE_VIEW_ORDER_BOARD,
  ]);

  const { acknowledgementStatus, acknowledgementId } = useSelector(
    (state: RootState) => state.orderReducer.order ?? ({} as Order)
  );
  const [requestChangeFinish, setRequestChangeFinish] = useState(false);

  const isPlural = pendingApprovalIds.length > 1;

  const shouldRenderSORActions = () => {
    return !isCurrentUserViewer && hasPendingSOA;
  };

  const shouldRenderDealerActions = () => {
    // 1. when latest ack status is pending
    // 2. cannot send ack (dealer) or user is CSR

    return (
      !isCurrentUserViewer &&
      latestACK?.status === ACKStatusEnum.PENDING &&
      (!canSendACK || isUserCSR)
    );
  };

  const canApproveOrDisapproveOnBehalfOfDealer = useHasPermissions(
    userPermissionsValues.CSR_VIEW_ORDERBOARD
  );

  const onRequestChangesACKSuccessHandler = (close?: () => void) => {
    toast.success(
      `You have successfully requested changes for this order.${
        isUserDealerOrSalesRep ? ' Please collaborate your changes.' : ''
      }`
    );

    dispatch(setCurtainTab(CurtainTabsEnum.COLLABORATION_TAB));
    dispatch(setOrderAckStatus(ACKStatusEnum.CHANGES_REQUESTED));

    if (close) {
      close();
      UtilService.onPopupClose();
    }
  };

  const onRequestChangesACK = (close?: () => void) => {
    if (latestACK) {
      dispatch(
        changeRequest({ acknowledgementId: latestACK.id, orderId }, () =>
          onRequestChangesACKSuccessHandler(close)
        )
      );
    }
  };

  const onCollaborationFinishRequestClickHandler = (close: () => void) => {
    setRequestChangeFinish(true);

    if (orderId && acknowledgementStatus) {
      dispatch(
        changesRequestFinsih(
          {
            orderId,
            acknowledgementId: acknowledgementId as string,
          },
          () => {
            close();
            dispatch(clearCurtain());
          },
          setRequestChangeFinish
        )
      );
    }
  };

  return (
    <>
      {/* dealer actions when latest ack exists */}
      {shouldRenderDealerActions() && !shouldRenderSORActions() && (
        <OrderFooter>
          <Container flex between>
            {shouldRenderDealerActions() && (
              <Wrapper flex middle>
                {/* NORMAL ACK */}
                <ConfirmationModal
                  confirmBgColor={isUserCSR ? anzac : undefined}
                  title="Request Changes"
                  buttonText={isUserCSR ? 'Yes' : 'Proceed and Collaborate'}
                  htmlMessage={
                    isUserCSR
                      ? `Are you sure you want to cancel this acknowledgment <strong>on behalf of the dealer</strong>?`
                      : `Request your changes in the collaboration window, then click 'Finish Request' to submit them. <br/><br/> <span style="color: ${lynch}; font-weight: 600;">Reminder:</span> Every topic created will be part of the change process within the acknowledgment that has been sent to you.`
                  }
                  confirm={onRequestChangesACK}
                  trigger={
                    <ButtonPrimary type="button">Request Changes</ButtonPrimary>
                  }
                />

                <Spacer w="24px" />

                <ButtonPrimary
                  bgColor={isUserCSR ? anzac : undefined}
                  onClick={() =>
                    history.push(`/order/${orderId}/acknowledgment`, {
                      scrollToBottom: true,
                    })
                  }
                >
                  Approve Acknowledgment
                </ButtonPrimary>
              </Wrapper>
            )}

            {children && (
              <Wrapper flex justifyEnd wFull={!shouldRenderDealerActions()}>
                {children}
              </Wrapper>
            )}
          </Container>
        </OrderFooter>
      )}

      {/* SPECIAL ORDER REQUEST FROM CSR */}
      {shouldRenderSORActions() && !shouldRenderDealerActions() && (
        <OrderFooter>
          <Container flex between>
            {shouldRenderSORActions() && (
              <Wrapper flex middle>
                <ButtonPrimary
                  onClick={() =>
                    history.push(`/order/${orderId}/acknowledgment`, {
                      highlightSpecialOrderApprovals: true,
                    })
                  }
                >
                  Request Changes
                </ButtonPrimary>

                <Spacer w="24px" />

                <ButtonPrimary
                  bgColor={
                    canApproveOrDisapproveOnBehalfOfDealer ? anzac : undefined
                  }
                  onClick={() =>
                    history.push(`/order/${orderId}/acknowledgment`, {
                      highlightSpecialOrderApprovals: true,
                    })
                  }
                >
                  Approve Special Order{isPlural ? 's' : ''}
                </ButtonPrimary>
              </Wrapper>
            )}
            {children && (
              <Wrapper flex justifyEnd wFull={!shouldRenderSORActions()}>
                {children}
              </Wrapper>
            )}
          </Container>
        </OrderFooter>
      )}

      {(acknowledgementStatus ?? 0) === ACKStatusEnum.CHANGES_REQUESTED && (
        <OrderFooter>
          <Wrapper flex between wFull>
            <ButtonPrimary
              onClick={(e) => {
                e.preventDefault();
                setOpenModalReqFinish(true);
              }}
            >
              Finish Request
            </ButtonPrimary>

            <Wrapper flex>{children}</Wrapper>
          </Wrapper>
        </OrderFooter>
      )}

      {(acknowledgementStatus ?? 0) !== ACKStatusEnum.CHANGES_REQUESTED &&
        !shouldRenderSORActions() &&
        !shouldRenderDealerActions() &&
        children && (
          <OrderFooter>
            <Wrapper flex justifyEnd wFull>
              {children}
            </Wrapper>
          </OrderFooter>
        )}

      <ConfirmationModal
        opened={openModalReqFinish}
        title="Finish Requesting Changes"
        message="If you are done requesting changes, click 'Finish Request' to submit them. To continue requesting changes, click 'Cancel'."
        buttonText="Finish Request"
        confirm={(close) => onCollaborationFinishRequestClickHandler(close)}
        disableLoading={requestChangeFinish}
        cancel={() => setOpenModalReqFinish(false)}
      />
    </>
  );
};

AcknowledgmentFooter.defaultProps = {
  withOrderLog: false,
};
